import { Link as AriaLink, type LinkProps as AriaLinkProps, composeRenderProps } from 'react-aria-components';

import { classnames } from '@/utils/classnames.ts';

export interface TextLinkProps extends AriaLinkProps {
    // Takes the text color, so a link in muted text stands out from the text around it.
    isEmphasized?: boolean;
    // Underlined and in the text color only while hovered, for links that are a title or a value of their own, e.g. in
    // a page's subtitle, where an underline on every one would be noisy.
    isQuiet?: boolean;
}

// For a react-aria element that reads as a link but does something else, e.g. a button in a page's subtitle that opens
// a dialog.
export const textLinkClassName = ({ isEmphasized = false, isQuiet = false }: Omit<TextLinkProps, 'className'>) =>
    classnames(
        'rounded-sm decoration-text-subtle underline-offset-4 outline-none hovered:decoration-text data-[focus-visible]:ring-2 data-[focus-visible]:ring-primary',
        // A link in a sentence takes the appearance's colour for links (plan task 120) where it has one.
        isQuiet ? 'hovered:text-text hovered:underline' : 'underline text-[color:var(--color-link,currentColor)]',
        isEmphasized && (isQuiet ? 'text-text' : 'text-[color:var(--color-link,var(--color-text))]')
    );

// A link within a sentence or a value, underlined so it reads as one.
const TextLink = ({ isEmphasized, isQuiet, className, ...props }: TextLinkProps) => (
    <AriaLink
        {...props}
        className={composeRenderProps(className, (className) =>
            classnames(textLinkClassName({ isEmphasized, isQuiet }), className)
        )}
    />
);

export default TextLink;
